import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { Attendee } from '../lib/types';

// Two stacked small lines of secondary text. Used in the roster row for the
// group cell and the emergency-contact cell. Renders nothing when both
// strings are empty so the surrounding flex container collapses cleanly.
export const TwoLines = ({
  line1,
  line2,
}: {
  line1: string;
  line2: string;
}) => (
  <>
    {line1 && (
      <Typography
        component='small'
        color={({ palette }) => palette.text.secondary}
        textAlign='left'
      >
        {line1}
      </Typography>
    )}
    {line2 && (
      <Typography
        component='small'
        color={({ palette }) => palette.text.secondary}
        textAlign='left'
        sx={{ fontSize: '0.75em' }}
      >
        {line2}
      </Typography>
    )}
  </>
);

// Renders the structured emergency contact as two lines: name (with the
// relationship in parens when present) on top, phone below. Returns null
// when nothing is set so the cell collapses cleanly.
export const EmergencyContactBlock = ({
  attendee,
}: {
  attendee: Attendee;
}) => {
  const name = attendee.emergency_contact_name?.trim() || '';
  const phone = attendee.emergency_contact_phone?.trim() || '';
  const relationship =
    attendee.emergency_contact_relationship?.trim() || '';
  if (!name && !phone) return null;
  const line1 = relationship ? `${name} (${relationship})` : name;
  return <TwoLines line1={line1} line2={phone} />;
};

// FA / MC chip with optional tooltip showing the detail text. When the flag
// is off, renders nothing. When on without a detail (legacy 'Yes'), renders
// a bare chip without a tooltip wrapper.
export const FlagChip = ({
  label,
  on,
  detail,
}: {
  label: string;
  on: boolean;
  detail: string;
}) => {
  if (!on) return null;
  const chip = (
    <Chip
      label={label}
      size='small'
      sx={{ fontSize: '0.7em', height: '20px' }}
      color='warning'
    />
  );
  if (!detail) return chip;
  return (
    <Tooltip
      title={detail}
      placement='top'
      slotProps={{ tooltip: { sx: { whiteSpace: 'pre-wrap' } } }}
    >
      <Box component='span' tabIndex={0} sx={{ display: 'inline-flex' }}>
        {chip}
      </Box>
    </Tooltip>
  );
};

// Case-insensitive name match for the search box. Matches both "First Last"
// and "Last, First" forms so volunteers can search either way.
export const matchesSearchQuery = (
  attendee: Attendee,
  lowerQuery: string,
): boolean => {
  if (!lowerQuery) return true;
  const first = attendee.first_name?.toLowerCase() ?? '';
  const last = attendee.last_name?.toLowerCase() ?? '';
  return (
    `${first} ${last}`.includes(lowerQuery) ||
    `${last}, ${first}`.includes(lowerQuery)
  );
};
